import * as AddToCartTracker from "@omni/services/tracking/trackers/feature/addToCart.tracker";
import * as RemoveFromCartTracker from "@omni/services/tracking/trackers/feature/removeFromCart.tracker";
import * as UpdateCartItemTracker from "@omni/services/tracking/trackers/feature/updateCartItem.tracker";
import { Cart } from "@omni/types/api/cart/bff/cart.model";
import { CartItem } from "@omni/types/api/cart/bff/shared/cartItem.model";
import { CheckoutStep } from "@omni/types/api/cart/bff/shared/enums/checkoutStep.enum";
import { Product } from "@omni/types/api/product/bff/product.model";
import { DeliveryType } from "@omni/types/api/product/bff/shared/enums/deliveryType.enum";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { PageTemplate } from "@omni/types/enums/pageTemplate.enum";
import { attempt } from "@omni/utils/function.utils";
import { MaybeType } from "@omni/utils/maybe.utils";
import { AxiosError } from "axios";
import queryString from "query-string";
import { ThunkDispatch } from "redux-thunk";

import CreateApiModule from "@/redux/create-api-module";
import {
    broadcastMessage,
    clearMessagesExcludingIds
} from "@/redux/modules/value/messages/messages.utils";
import {
    IState,
    TActionOptions,
    TApiAction,
    TExtraThunkArgs
} from "@/redux/store.types";
import { getApiDataState } from "@/redux/utils";
import CallService, { CallServiceKey } from "@/services/call.service";
import {
    StorePersistenceStrategy,
    StoreService
} from "@/services/store.service";
import { determineApiMessages } from "@/utils/apiMessages";
import { Config } from "@/utils/config.utils";
import { isPageTemplate } from "@/utils/pageTemplate.utils";

import {
    $CartModuleName,
    AddToCartBulkRequestBody,
    AddToCartRequestBody,
    CartItemConfigUpdateOrigin,
    CheckoutStepValidationResult
} from "./@types/cart.types";
import { DISCRIMINATORS } from "./cart.discriminators";
import { returnMaybeCartModel } from "./cart.guard";
import {
    recalculateRemovableQuantity,
    validateAndRedirect
} from "./cart.helpers";
import { CartMapper } from "./cart.mapper";
import CartReducer from "./cart.reducer";
import { getCart } from "./cart.selectors";

const { actions, constants } = CreateApiModule(
    $CartModuleName,
    ({ url }) => url,
    {
        reducer: CartReducer,
        mapper: CartMapper,
        transformResponse: (data?: any) => {
            const mayBeCart = returnMaybeCartModel(data);
            if (mayBeCart.type === MaybeType.Just) {
                manageApiMessages(mayBeCart.value);
                manageSalesIncentives(mayBeCart.value);

                const shopId = mayBeCart.value.shopId;

                if (shopId) {
                    StoreService.setStoreCode(
                        StorePersistenceStrategy.Cart,
                        shopId
                    );
                } else {
                    StoreService.removeStoreCode(StorePersistenceStrategy.Cart);
                }
            } else {
                StoreService.removeStoreCode(StorePersistenceStrategy.Cart);
            }

            return data;
        }
    }
);

export const clear = actions.clear;
export const refetch = actions.refetch;
export { constants };

const checkCartCall = (action?: TApiAction) => {
    if (action?.type === constants.SUCCESS) {
        const mayBeCart = returnMaybeCartModel(action.payload);
        if (
            mayBeCart.type === MaybeType.Just &&
            mayBeCart.value.items.length > 0
        ) {
            CallService.enable(CallServiceKey.Cart);
        } else {
            CallService.disable(CallServiceKey.Cart);
        }
    } else {
        // always enable on error
        CallService.enable(CallServiceKey.Cart);
    }
};

// explicit code path for CTA.CHANGE_STORE_AND_CNC
export const updateStoreAndDeliveryType =
    (
        deliveryType: string,
        shop: string | null,
        sku: string,
        force: boolean = false
    ) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) =>
        await dispatch(
            actions.load(
                {
                    path: {
                        url: `/${Config.general.basestore}/carts/items`,
                        bff: true
                    },
                    data: {
                        deliveryType,
                        quantity: 0, // don't increase product count
                        shopId:
                            deliveryType === DeliveryType.ClickAndCollect &&
                            shop
                                ? shop
                                : null,
                        sku
                    }
                },
                "POST",
                force
            )
        );

const addToCart =
    (
        {
            deliveryType,
            shopId,
            quantity,
            sku,
            additionalServices
        }: AddToCartRequestBody,
        force: boolean = false,
        productSubType?: string,
        extraConfiguration?: unknown
    ) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) => {
        const action = await dispatch(
            actions.load(
                {
                    path: {
                        url: `/${Config.general.basestore}/carts/items${
                            productSubType ? `/${productSubType}` : ""
                        }`,
                        bff: true
                    },
                    data: {
                        ...(extraConfiguration ?? {}),
                        deliveryType: deliveryType,
                        sku: sku,
                        quantity: quantity,
                        additionalServices: additionalServices,
                        shopId:
                            deliveryType === DeliveryType.ClickAndCollect &&
                            shopId
                                ? shopId
                                : null
                    }
                },
                "POST",
                force
            )
        );

        checkCartCall(action);

        return action;
    };

export const addProductTileProductToCart =
    (
        addToCartRequest: AddToCartRequestBody,
        product: Product<ProductFieldSet.ProductTile>,
        force: boolean = false,
        productSubType?: string,
        extraConfiguration?: Record<string, unknown>
    ) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) => {
        return addToCart(
            addToCartRequest,
            force,
            productSubType,
            extraConfiguration
        )(dispatch).then((action) => {
            if (action.type === constants.SUCCESS) {
                AddToCartTracker.trackProductTileProduct(
                    product,
                    addToCartRequest.quantity
                );
            }

            return action;
        });
    };

export const addECommerceProductToCart =
    (
        addToCartRequest: AddToCartRequestBody,
        product: Product<ProductFieldSet.Full>,
        force: boolean = false,
        productSubType?: string,
        extraConfiguration?: unknown
    ) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) => {
        return addToCart(
            addToCartRequest,
            force,
            productSubType,
            extraConfiguration
        )(dispatch).then((action) => {
            if (action.type === constants.SUCCESS) {
                AddToCartTracker.trackECommerceProduct(
                    product,
                    addToCartRequest.quantity
                );
            }

            return action;
        });
    };

export const addToCartBulk =
    (
        addToCartBulk: AddToCartBulkRequestBody,
        trackingProducts: Array<{
            product?: Product<ProductFieldSet.ProductTile>;
            quantity: number;
        }>,
        force: boolean = false
    ) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) => {
        const action = await dispatch(
            actions.load(
                {
                    path: {
                        url: `/${Config.general.basestore}/carts/items-bulk`,
                        bff: true
                    },
                    data: addToCartBulk
                },
                "POST",
                force
            )
        );

        if (action.type === constants.SUCCESS && trackingProducts.length > 0) {
            trackingProducts.forEach(
                ({ product, quantity }) =>
                    product &&
                    AddToCartTracker.trackProductTileProduct(product, quantity)
            );
        }

        checkCartCall(action);

        return Promise.resolve(action);
    };

export const removeFreeArticleFromCart =
    (id: string, productJson: CartItem, force: boolean = false) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) => {
        const action = await dispatch(
            actions.load(
                {
                    path: {
                        url: `/${Config.general.basestore}/carts/cta/removefreearticle/${id}`,
                        bff: true
                    }
                },
                "PATCH",
                force
            )
        );

        if (action.type === constants.SUCCESS && productJson) {
            RemoveFromCartTracker.trackCartItem(
                productJson,
                productJson.quantity
            );
        }

        checkCartCall(action);

        return Promise.resolve(action);
    };

export const addConfigurableProductToCart =
    (data: any, endPoint: string) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) => {
        const action = await dispatch(
            actions.load(
                {
                    path: {
                        url: `/${Config.general.basestore}${endPoint}`,
                        bff: true
                    },
                    data: data
                },
                "POST",
                true,
                { mergeParams: false }
            )
        );

        if (action.type === constants.SUCCESS) {
            // todo add to cart, to be determined after backend implementation
            // trackAddToCartImpression(productObject, quantity);
        }

        checkCartCall(action);

        return Promise.resolve(action);
    };

export const removeFromCart =
    (id: string, quantityToRemove: number, free: boolean) =>
    async (
        dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>,
        getState: () => IState
    ) => {
        const cart = getCart(getState())!;

        const productJson = cart.items.find((item) => item.id === id);

        const newQuantity = recalculateRemovableQuantity(
            cart,
            id,
            quantityToRemove
        );

        const action = await dispatch(
            actions.load(
                {
                    path: {
                        url: `/${Config.general.basestore}/carts/items/${id}`,
                        bff: true
                    },
                    data: {
                        quantityToRemove: newQuantity,
                        free
                    }
                },
                "DELETE",
                true
            )
        );

        if (action.type === constants.SUCCESS && productJson) {
            RemoveFromCartTracker.trackCartItem(
                productJson,
                productJson.quantity
            );
        }

        checkCartCall(action);

        return Promise.resolve(action);
    };

export const changeStore =
    (store: string, force: boolean = false) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) =>
        await dispatch(
            actions.load(
                {
                    path: {
                        url: `/${Config.general.basestore}/carts/cta/changestore`,
                        bff: true
                    },
                    data: { store }
                },
                "PATCH",
                force
            )
        );

export const changeStoreToECorner =
    (force: boolean = false) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) =>
        await dispatch(
            actions.load(
                {
                    path: {
                        url: `/${Config.general.basestore}/carts/cta/changestore/ecorner`,
                        bff: true
                    }
                },
                "PATCH",
                force
            )
        );

export const updateCartItem =
    (
        id: string,
        quantity: number,
        deliveryType: string,
        product?: CartItem,
        quantityDelta?: number,
        additionalServices?: Array<string>,
        actionOptions: TActionOptions = { mergeParams: true }
    ) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) => {
        const action = await dispatch(
            actions.load(
                {
                    path: {
                        url: `/${Config.general.basestore}/carts/items/${id}`,
                        bff: true
                    },
                    data: {
                        quantity,
                        deliveryType,
                        additionalServices
                    }
                },
                "PATCH",
                true,
                actionOptions
            )
        );

        if (action.type === constants.SUCCESS && product) {
            UpdateCartItemTracker.trackCartItem(product, quantityDelta ?? 0);
        }

        checkCartCall(action);

        return Promise.resolve(action);
    };

export const updateCartItemConfig =
    (
        id: string,
        quantity: number,
        deliveryType: string,
        config: Record<string, unknown>,
        product: CartItem,
        productSubType: string,
        origin: CartItemConfigUpdateOrigin,
        shopId?: string
    ) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) => {
        const action = await dispatch(
            actions.load(
                {
                    path: {
                        url: `/${Config.general.basestore}/carts/items/${productSubType}/${id}/${origin}`,
                        bff: true
                    },
                    data: {
                        ...config,
                        shopId,
                        deliveryType,
                        quantity,
                        sku: product.sku
                    }
                },
                "PATCH",
                true
            )
        );

        return Promise.resolve(action);
    };

export const checkout =
    (
        shopId: string,
        conditionMinAgeChecked: boolean,
        conditionPartnerEmailChecked: boolean,
        bonusCardNumber?: string,
        deliveryAddressId?: string,
        billingAddressId?: string,
        acceptedAdditionalServiceDocumentCodes: Array<string> = []
    ) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) => {
        return dispatch(
            actions.load(
                {
                    path: {
                        url: `/${Config.general.basestore}/checkout`,
                        bff: true
                    },
                    data: {
                        deliveryAddressId,
                        billingAddressId,
                        shopId,
                        conditionMinAgeChecked,
                        conditionPartnerEmailChecked,
                        acceptedAdditionalServiceDocumentCodes,
                        ...(bonusCardNumber ? { bonusCardNumber } : {})
                    }
                },
                "POST",
                true
            )
        );
    };

export const addPromotionCode =
    (code: string, bonusCardNumber?: string, force: boolean = false) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) =>
        await dispatch(
            actions.load(
                {
                    path: {
                        url: `/${Config.general.basestore}/carts/promotioncodes`,
                        bff: true
                    },
                    data: {
                        code,
                        ...(bonusCardNumber ? { bonusCardNumber } : {})
                    }
                },
                "POST",
                force,
                {
                    discriminator: DISCRIMINATORS.PromotionCode
                }
            )
        );

export const removePromotionCode =
    (code: string, force: boolean = false) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) =>
        await dispatch(
            actions.load(
                {
                    path: {
                        url: `/${Config.general.basestore}/carts/promotioncodes/${code}`,
                        bff: true
                    }
                },
                "DELETE",
                force,
                {
                    discriminator: DISCRIMINATORS.PromotionCode
                }
            )
        );

export const addCoupon =
    (
        requestData: {
            code: string;
            recaptcha: string;
            bonusCardNumber?: string | null;
        },
        force: boolean = false
    ) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) => {
        return dispatch(
            actions.load(
                {
                    path: {
                        url: `/${Config.general.basestore}/carts/coupons`,
                        bff: true
                    },
                    data: {
                        ...requestData,
                        ...(requestData.bonusCardNumber
                            ? { bonusCardNumber: requestData.bonusCardNumber }
                            : {})
                    }
                },
                "POST",
                force,
                {
                    skipErrors: [404]
                }
            )
        );
    };

export const removeCoupon =
    (cardCode: string, force: boolean = false) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) => {
        return dispatch(
            actions.load(
                {
                    path: {
                        url: `/${Config.general.basestore}/carts/coupons/${cardCode}`,
                        bff: true
                    }
                },
                "DELETE",
                force
            )
        );
    };

export const acceptNegativeDeliveryPromise =
    (sku: string | undefined, force: boolean = false) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) =>
        await dispatch(
            actions.load(
                {
                    path: {
                        url: `/${Config.general.basestore}/carts/cta/acceptnegativedeliverypromise`,
                        bff: true
                    },
                    data: { sku }
                },
                "PATCH",
                force
            )
        );

export const acceptPositiveDeliveryPromise =
    (sku: string, force: boolean = false) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) =>
        await dispatch(
            actions.load(
                {
                    path: {
                        url: `/${Config.general.basestore}/carts/cta/acceptpositivedeliverypromise`,
                        bff: true
                    },
                    data: { sku }
                },
                "PATCH",
                force
            )
        );

export const load =
    (force: boolean = false) =>
    async (
        dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>,
        getState: () => IState
    ) => {
        if (
            !CallService.shouldCall(CallServiceKey.Cart, { minutes: 5 }) &&
            !force
        ) {
            return {
                type: constants.SUCCESS,
                payload: getApiDataState(getState(), $CartModuleName)?.data,
                actionOptions: {
                    discriminator: DISCRIMINATORS.Load,
                    skipErrors: [400]
                }
            } as TApiAction;
        }

        const parsedQueryString = queryString.parse(window.location.search);

        const abandonedCartData = attempt(() => {
            const data = parsedQueryString?.abandonedCartData as string | null;
            if (!data) {
                return undefined;
            }

            return JSON.parse(atob(data));
        }, undefined);

        let loadResult;

        if (isPageTemplate(PageTemplate.ShoppingCart) && abandonedCartData) {
            loadResult = await dispatch(
                actions.load(
                    {
                        path: {
                            url: `/${Config.general.basestore}/carts/abandonedCart`,
                            bff: true
                        },
                        data: abandonedCartData
                    },
                    "POST",
                    force,
                    {
                        discriminator: DISCRIMINATORS.Load,
                        skipErrors: [400]
                    }
                )
            );
            const { abandonedCartData: _, ...rest } = parsedQueryString;
            const stringifiedQueryParams = attempt(
                () => queryString.stringify(rest),
                ""
            );
            window.history.pushState(null, "", `?${stringifiedQueryParams}`);
        } else {
            loadResult = await dispatch(
                actions.load(
                    {
                        path: {
                            url: `/${Config.general.basestore}/carts`,
                            bff: true
                        }
                    },
                    "GET",
                    force,
                    {
                        discriminator: DISCRIMINATORS.Load,
                        skipErrors: [400]
                    }
                )
            );
        }

        checkCartCall(loadResult);

        return Promise.resolve(loadResult);
    };

export const startCheckout =
    () =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) => {
        return await dispatch(
            actions.load(
                {
                    path: {
                        url: `/${Config.general.basestore}/carts`,
                        bff: true
                    }
                },
                "GET",
                true,
                {
                    discriminator: DISCRIMINATORS.StartCheckout,
                    skipErrors: [400]
                }
            )
        );
    };

export type GiftCardRequest = {
    sku: string;
    quantity: number;
    amountInEuros: number;
    personalMessage?: string | null;
};

export const addGiftCardToCart =
    (request: GiftCardRequest) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) => {
        const loadResult = await dispatch(
            actions.load(
                {
                    path: {
                        url: `/${Config.general.basestore}/carts/items/giftcards`,
                        bff: true
                    },
                    data: request
                },
                "POST",
                true
            )
        );

        checkCartCall(loadResult);

        return loadResult;
    };

export const updateGiftCardInCart =
    (id: string, request: GiftCardRequest) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) =>
        await dispatch(
            actions.load(
                {
                    path: {
                        url: `/${Config.general.basestore}/carts/items/giftcards/${id}`,
                        bff: true
                    },
                    data: request
                },
                "PATCH",
                true
            )
        );

export const validateCheckoutStep =
    (checkoutStep: CheckoutStep, force: boolean = false) =>
    async (
        dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>
    ): Promise<CheckoutStepValidationResult> =>
        dispatch(
            actions.load(
                {
                    path: {
                        url: `/${Config.general.basestore}/carts`,
                        bff: true
                    }
                },
                "GET",
                force,
                {
                    discriminator: DISCRIMINATORS.Load
                }
            )
        ).then((loadResult) => {
            checkCartCall(loadResult);

            const payload = loadResult.payload;

            const cart = (
                (payload as { error: AxiosError })?.error ? undefined : payload
            ) as Cart | undefined;

            return validateAndRedirect(checkoutStep)(cart);
        });

const manageSalesIncentives = (shoppingCart: Cart) => {
    if (shoppingCart.salesIncentives.length > 0) {
        broadcastMessage({
            id: "sales-incentive",
            status: "primary",
            text: shoppingCart.salesIncentives,
            dismissible: false
        });
    }
};

const manageApiMessages = (shoppingCart: Cart) => {
    clearMessagesExcludingIds([
        "general-cart",
        "carts-merged",
        "payment-error-message",
        "ecorner-public",
        "update-giftcard-error",
        "sale-incentive"
    ]);

    const itemHasMessage = shoppingCart.items.filter(
        (item) => item.messages !== null
    );

    const mergedMessages = [];

    if (shoppingCart.messages.length > 0 || itemHasMessage.length > 0) {
        mergedMessages.push(...shoppingCart.messages);
        determineApiMessages(mergedMessages, shoppingCart.items);
    }
};

export const APIStatus = constants;
